/**
 * useUnblockStorage - Hook para desbloquear storages individuales
 * 
 * Permite desbloquear un storage específico eliminando su registro de bloqueo.
 * Realiza DELETE a /invoices/storages/{id}/block donde id es el ID del registro de bloqueo
 * (accounting_storage_blocks.id), no el storage_id.
 * Muestra toasts de éxito/error y ejecuta callback de refetch al completar exitosamente.
 * 
 * @returns {Object} { unblockStorage: función de desbloqueo, isUnblocking: estado de carga }
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { EligibleStorageApiAdapter } from '../adapters/EligibleStorageApiAdapter';
import { apiClientWH } from '@shared/services/ApiClient';
import { createHttpClient } from '@adapters/HttpClient';

export const useUnblockStorage = () => {
  const { t } = useTranslation('eligibleStorageModule');
  const { toast } = useToast();
  const [isUnblocking, setIsUnblocking] = useState(false);

  const unblockStorage = useCallback(
    async (
      blockId: number, // ID del registro de bloqueo (accounting_storage_blocks.id)
      onSuccess?: () => void
    ) => {
      setIsUnblocking(true);

      try {
        const storagesAdapter = new EligibleStorageApiAdapter(
          createHttpClient(apiClientWH)
        );
        await storagesAdapter.unblockStorage(blockId);

        // 🎯 Si llegamos aquí, la API devolvió 200 OK = éxito
        toast({
          title: t('toast.unblock_success.title', 'Storage desbloqueado'),
          description: t('toast.unblock_success.description', 'El storage se ha desbloqueado exitosamente'),
          variant: 'default',
        });

        // 🎯 Ejecutar callback de éxito (refetch de datos)
        onSuccess?.();
      } catch (error: any) {
        // 🎯 Extraer mensaje amigable de la API
        const apiMessage: string | undefined = error?.response?.data?.message;
        const apiErrors: Record<string, string[] | string> | undefined =
          error?.response?.data?.errors;

        let description = apiMessage;

        // Unir los errores detallados si existen
        if (apiErrors && typeof apiErrors === 'object') {
          const details = Object.values(apiErrors)
            .flat()
            .filter(Boolean)
            .join(' • ');
          description = description ? `${description} — ${details}` : details;
        }

        // Fallback de mensaje
        if (!description) {
          description =
            error?.message ||
            t(
              'toast.unblock_error.unexpected',
              'Error inesperado al desbloquear el storage. Inténtalo de nuevo.'
            );
        }

        toast({
          title: t('toast.unblock_error.title', 'Error al desbloquear'),
          description,
          variant: 'destructive',
        });
      } finally {
        setIsUnblocking(false);
      }
    },
    [t, toast]
  );

  return {
    unblockStorage,
    isUnblocking,
  };
};

